/* ================================================================
   BRITTEN CUSTOM: Real Brides archive v2 (preview) — core
   Location: assets/britten-archive.js
   Called from: snippets/britten-archive.liquid (templates/page.real-
                brides-v2.liquid), a HIDDEN preview page for owner review
                against the two live pages (/pages/real-brides,
                /pages/real-wedding-photos) — see britten-ugc-audit2.md.

   This file owns everything shared between the two views: state, the URL
   (query params, not hash — see below), search interpretation, the header
   (title, count, view switch, search box, interpretation chips), tracking,
   and the filtering predicates both views render from. It owns NO
   rendering of either view's own list/grid — that's assets/britten-
   archive-weddings.js and assets/britten-archive-photos.js, each of which
   calls BrittenArchive.registerView() to hook in. "Match colours" (round 1
   owner feedback) is gone. The "Try:" examples came back in round 3, on
   desktop only (CSS hides the row on mobile, while the upload panel is
   open and while upload results show): a click fills the search box and runs it, nothing more. The
   header's upload trigger, panel and routing (palette extractor + dress
   match) live in assets/britten-archive-weddings.js ("Upload") — it is the
   one that knows dress match; this file only offers it two small hooks,
   onChange() and onClearAll() (see below), so "Clear all" also resets an
   upload's results and its path cards follow the view.

   Why one shared state/URL layer: the audit's top findings were all about
   the SAME filter meaning different things (or vanishing) depending on
   which of the two old pages you were on. Doing state/URL/search once,
   here, and having both views read from it, is the fix.

   State -> URL contract (query string, not hash — the two old pages used
   incompatible schemes, hash-only and mixed hash/query; one page, one
   scheme):
     view       'photos' (default, omitted) | 'weddings'
     q          free-text search (already-interpreted leftover, e.g. a
                place name that matched no facet)
     product    a product handle (single-select)
     setting, season, country, palette   shared facets (single-select each)
     silhouette, veil_length             Weddings-only facets — NOT in the
                approved param list, added here because the spec's own
                "Wedding details / Veil details" filter groups need
                something to hold in the second group. Additive: every
                param in the approved list still means exactly what it
                says; these two are extra, shareable state, nothing more.
     lens, colour   Photos-only (moment, colour sort). Kept in state and in
                the URL even while viewing Weddings — see the disabled
                chip in britten-archive-weddings.js — never dropped.
     p          open photo id (lightbox) — assets/britten-archive-lightbox.js
     w          open wedding slug (lightbox scoped to one wedding's own
                photos, or a request to open her page) — same file.

   Facet selections are SINGLE-select in this build (one value per key),
   not the old page's comma multi-select. That's a deliberate scope cut
   for this preview, not an oversight — see the PR description.

   Filters use history.replaceState. Switching view and opening the
   lightbox use history.pushState (so Back/Forward step through those, but
   don't spam history for every facet click) — see writeUrl()/pushUrl()
   and openPhoto()/openWedding()/closeOverlay() below.

   NO remote calls. The old page's concierge asked a Supabase edge function
   (with an inline anon key) to refine a search server-side; this preview
   is local-code-only, so search interpretation is 100% client-side
   (interpretQuery below, ported from britten-real-brides-page.liquid's
   same-named function, with the "replace, don't merge" and "exclusion is
   explained, not inverted" audit fixes built in from the start).

   Language (2 Oct 2026): every customer-facing string is a `britten.js.rb.
   archive_*` key (or an existing one whose English is identical), read
   through safeI18n with the English literal as the fallback, so English
   output is unchanged. On a page that is not English this file also loads
   the language's text file (assets/britten-real-brides.<lang>.json, URL from
   window.BrittenRB.assetUrl(), see snippets/britten-rb-locale.liquid) and
   merges it for DISPLAY only: one-sentence summaries (`sm`, by wedding slug),
   facet names and facet value labels. State, URL, filters and search always
   run on the stored English slugs and codes; a missing or broken file leaves
   the page in English. Country names come from Intl.DisplayNames, by ISO code.
   Search: accents are folded (so "jardín" is not mangled into "jard n") and a
   per-language list of search words (britten.rb.archive_syn_*) rewrites the
   query into the English words interpretQuery() already understands.

   CHANGELOG:
   - 2 Oct 2026: localisation (see "Language" above). interpretQuery() itself is
     unchanged apart from the accent fold; runSearch() takes an optional second
     argument, the English query a translated "Try" example stands for.
   - 2026-09-24: initial build (PR 1 of 3 — core, state/URL/search, the
     Weddings view). Photos view and the shared lightbox land in the next
     two PRs on top of this branch.
   - 2026-09-29: round 3 owner feedback — the count line is always both
     counts from the JSON ("406 weddings · 1,992 photographs"), whichever
     view is active and whatever is filtered (the filtered count lives in
     the Photos controls bar); "Try" examples wired (desktop only, CSS);
     onChange()/onClearAll() hooks for the shared upload. No state, URL,
     search-interpretation or tracking change.
   - 2026-09-29: round 4 — the count line is server-rendered now (the
     generated britten-archive-counts snippet), so renderHeader() no longer
     blanks it while the JSON is still loading; it only rewrites it once the
     numbers are known. New onPhotosReady() hook so the Weddings cards can
     fill their thumbnail strips when the (lazy) photos JSON lands, and
     openPhoto()/registerOverlay unchanged for them. Nothing else in state,
     URL, search or tracking changed.
   - 2026-09-29: round 4 upload results — switchView() takes { replace: true }
     (replaceState instead of pushState) so the upload's automatic pick of the
     Weddings or Photos path does not add a Back step. Nothing else changed.
   - 2026-09-29: press marks — press()/pressMark()/pressBadge() read the optional
     `px` on a wedding ({b,o,u}) and the logos from snippets/britten-press-marks
     .liquid (JSON script, parsed once). Used by the Photos tiles, Weddings cards
     and the lightbox. Absent `px` = null everywhere; nothing else changed.
   - 29 Sep 2026: track() adds rb_sid to every payload (window.BrittenRb, only when
     analytics consent allows one) and, on lightbox_shop_click, writes the cart's
     rb_source = 'archive' (first touch wins). See assets/britten-rb-session.js.
   - 6 Oct 2026: photoAlt() — a photo's alt text. English is the generated `a` as
     written; any other language is built from britten.rb.archive_alt_* and the
     photo's own data (veil name, region or country). Used by the Photos tiles and
     the lightbox, which read `p.a` before.
   - 6 Oct 2026 (2): photoAlt() names a piece by its short name (a veil through
     rb.archive_alt_veil), and a region that is only the country's name reads as
     the country in the page's language.
   - 12 Oct 2026: the Photos tab opens newest first. canonicalPhotoOrder() sorts
     the photos with no opening rank by their wedding's archive number (`an`,
     highest first) before quality and id, then interleaves as before, so the
     round-robin starts with the newest wedding. The photos JSON now waits for
     the archive JSON (archiveSettled) so the weddings are there to look up.
   ================================================================ */
(function () {
  'use strict';

  // === CONFIGURATION ===
  var FEATURE_ENABLED = true;
  var DEBUG_MODE = false;

  function log(msg) {
    if (DEBUG_MODE && window.console) console.log('[Britten Archive] ' + msg);
  }

  if (!FEATURE_ENABLED) return;

  var root = document.querySelector('.bra');
  if (!root) return;

  // ---------------------------------------------------------------
  // Language. The receiver has to end in "i18n" for scripts/i18n-js-keys-
  // check.py to find the call sites. Every call carries its English literal
  // as the fallback, so English reads exactly as it always has.
  // ---------------------------------------------------------------
  var i18n = window.BrittenI18n;
  var safeI18n = {
    t: function (key, vars) { return i18n ? i18n.t(key, vars) : ''; },
    n: function (key, count, vars) { return i18n ? i18n.n(key, count, vars) : ''; }
  };
  var RB = window.BrittenRB; // only rendered on a page that is not English (snippets/britten-archive.liquid)
  var LANG = String((RB && RB.locale) || (i18n && i18n.locale) || 'en').toLowerCase().split('-')[0];
  var IS_EN = !LANG || LANG === 'en';
  function hasOwn(o, k) { return Object.prototype.hasOwnProperty.call(o, k); }
  function plainObject(o) { return (o && typeof o === 'object' && !Array.isArray(o)) ? o : {}; }
  // Whole numbers as the page's language writes them. English keeps en-GB.
  function fmt(n) {
    var N = window.BrittenNumber;
    if (!IS_EN && N && typeof N.format === 'function') return N.format(n || 0);
    return (n || 0).toLocaleString('en-GB');
  }
  // A country's name in the page's language, from its ISO code ('' = no better name).
  var regionNames = null;
  function regionName(cc) {
    if (IS_EN || !cc) return '';
    try {
      if (regionNames === null) regionNames = (typeof Intl !== 'undefined' && Intl.DisplayNames) ? new Intl.DisplayNames([LANG], { type: 'region' }) : false;
      var n = regionNames ? regionNames.of(cc) : '';
      return n && n !== cc ? n : '';
    } catch (e) { regionNames = false; return ''; }
  }

  // ---------------------------------------------------------------
  // Tracking — same Shopify.analytics.publish pattern as every other
  // Britten feature (see docs/veil-guide-custom-pixel.md; new event names
  // for this page are appended there). Flat payloads only.
  // ---------------------------------------------------------------
  var EVENT_PREFIX = 'britten_archive_';
  // A click that leads to a product page: the cart remembers the archive as where she started.
  var SHOP_EVENTS = { lightbox_shop_click: true };
  function track(name, data) {
    var eventName = EVENT_PREFIX + name;
    var payload = data || {};
    // rb_sid and the cart's rb_source come from assets/britten-rb-session.js, which the
    // dress match block loads first (it is absent, or has no id without analytics consent:
    // then the event simply goes without).
    try {
      if (window.BrittenRb) {
        window.BrittenRb.withSid(payload);
        if (SHOP_EVENTS[name]) window.BrittenRb.touch('archive');
      }
    } catch (e) { /* never break the page */ }
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(eventName, payload);
      }
    } catch (e) { /* analytics must never break the page */ }
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, payload);
      } else if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push({ event: eventName, britten: payload });
      }
    } catch (e) { /* analytics must never break the page */ }
    log(eventName + ' ' + JSON.stringify(payload));
  }

  // ---------------------------------------------------------------
  // State
  // ---------------------------------------------------------------
  var SHARED_FACET_KEYS = ['setting', 'season', 'country', 'palette'];
  var WEDDING_FACET_KEYS = ['silhouette', 'veil_length'];
  var ALL_FACET_KEYS = SHARED_FACET_KEYS.concat(WEDDING_FACET_KEYS);
  var STATE_KEYS = ['view', 'q', 'product'].concat(ALL_FACET_KEYS, ['lens', 'colour', 'p', 'w']);

  function blankState() {
    var s = { view: 'photos' };
    STATE_KEYS.forEach(function (k) { if (!(k in s)) s[k] = ''; });
    return s;
  }
  var state = blankState();

  // ---------------------------------------------------------------
  // URL <-> state
  // ---------------------------------------------------------------
  function currentUrl() { return new URL(window.location.href); }

  function stateFromUrl(url) {
    var s = blankState();
    var sp = url.searchParams;
    // NB: never `view` in the URL — Shopify reserves ?view= to pick an
    // alternate page template, so ?view=weddings rendered a blank page.
    s.view = sp.get('show') === 'weddings' ? 'weddings' : 'photos';
    STATE_KEYS.forEach(function (k) {
      if (k === 'view') return;
      var v = sp.get(k);
      if (v) s[k] = v;
    });
    return s;
  }

  function urlForState() {
    var url = currentUrl();
    var sp = url.searchParams;
    if (state.view === 'weddings') sp.set('show', 'weddings'); else sp.delete('show');
    STATE_KEYS.forEach(function (k) {
      if (k === 'view') return;
      if (state[k]) sp.set(k, state[k]); else sp.delete(k);
    });
    return url.pathname + (sp.toString() ? '?' + sp.toString() : '');
  }

  // Plain filter/view-state writes. `push` follows the spec: filters
  // replace, the view switch pushes. Overlay opens (lightbox) use
  // writeOverlayUrl below instead, which additionally tracks whether THIS
  // page pushed the entry, so Close knows whether history.back() is safe.
  function writeUrl(push) {
    var next = urlForState();
    if (push) history.pushState({ braNav: true }, '', next);
    else history.replaceState(history.state, '', next);
  }

  // Same URL, but stamps (or carries forward) braPushed so closeOverlay()
  // only ever calls history.back() for an entry this page itself pushed —
  // a direct ?p=/?w= arrival must close to the archive, not leave it
  // (audit: "direct-photo close"). Mirrors showLightbox() in
  // britten-picture-desk.js.
  function writeOverlayUrl(push) {
    var next = urlForState();
    if (push) {
      history.pushState({ braNav: true, braOverlay: true, braPushed: true }, '', next);
    } else {
      var pushed = !!(history.state && history.state.braPushed);
      history.replaceState({ braNav: true, braOverlay: true, braPushed: pushed }, '', next);
    }
  }

  // Old-page deep links: /pages/real-brides#w=slug, #q=text, #product=handle.
  // Read once at boot, folded into state, then the hash is dropped (query
  // params are this page's one scheme — see file header).
  function absorbLegacyHash() {
    var h = (location.hash || '').replace(/^#/, '');
    if (!h) return false;
    var changed = false;
    h.split('&').forEach(function (kv) {
      var parts = kv.split('=');
      if (parts.length !== 2 || !parts[1]) return;
      var key = decodeURIComponent(parts[0]);
      var val = decodeURIComponent(parts[1]);
      if (key === 'w' && !state.w) { state.w = val; changed = true; }
      else if (key === 'q' && !state.q) { state.q = val; changed = true; }
      else if (key === 'product' && !state.product) { state.product = val; changed = true; }
    });
    return changed;
  }

  // ---------------------------------------------------------------
  // Data
  // ---------------------------------------------------------------
  var data = { archive: null, photos: null };
  var productIndexByHandle = {};
  var facetDefs = []; // [{key, label, dataKey, group:'shared'|'wedding'|'veil', options:[{v,label}]}]

  // The language's text file, merged for display only (see "Language" in the
  // header): { labels: {facet: name}, values: {facet: {slug: label}} } or null.
  var langText = null;
  function fetchOverlay() {
    var url = '';
    try { url = (!IS_EN && RB && typeof RB.assetUrl === 'function') ? RB.assetUrl() : ''; } catch (e) { url = ''; }
    if (!url) return Promise.resolve(null);
    return fetch(url).then(function (r) {
      if (!r.ok) throw new Error('status ' + r.status);
      return r.json();
    }).then(function (t) {
      return (t && typeof t === 'object') ? t : null;
    }).catch(function (e) { log('language file unavailable: ' + e); return null; });
  }
  // Summaries are copied onto the weddings (nothing searches or filters on
  // `sm`); the facet names and value labels are only ever read where printed.
  function applyOverlay(archive, t) {
    if (!t || !archive) return;
    langText = { labels: plainObject(t.labels), values: plainObject(t.values) };
    var sums = plainObject(t.weddings);
    (archive.weddings || []).forEach(function (w) {
      var tr = hasOwn(sums, w.s) ? sums[w.s] : null;
      if (tr && typeof tr.sm === 'string' && tr.sm.length) w.sm = tr.sm;
    });
  }
  // A facet's name. English: the JSON's own. Otherwise the language file, then
  // a locale key where the file has none, then English.
  function facetLabel(dataKey, english) {
    if (IS_EN) return english;
    var s = langText && hasOwn(langText.labels, dataKey) ? langText.labels[dataKey] : '';
    if (typeof s === 'string' && s) return s;
    if (dataKey === 'setting') s = safeI18n.t('js.rb.archive_facet_setting');
    else if (dataKey === 'country') s = safeI18n.t('js.rb.country_facet_name');
    return s || english;
  }

  function valueLabel(key, v) {
    if (!IS_EN) {
      var cn = key === 'country' ? regionName(v) : '';
      if (cn) return cn;
      var group = langText && hasOwn(langText.values, key) ? plainObject(langText.values[key]) : null;
      var ov = group && hasOwn(group, v) ? group[v] : '';
      if (typeof ov === 'string' && ov) return ov;
    }
    var f = data.archive && data.archive.facets;
    var vl = (f && f.valueLabels && f.valueLabels[v]) || null;
    if (vl) return vl;
    if (key === 'country') return (f && f.countryNames && f.countryNames[v]) || v;
    return String(v).replace(/_/g, ' ').replace(/^./, function (c) { return c.toUpperCase(); });
  }

  function buildFacetDefs() {
    var f = data.archive.facets || {};
    var labels = f.labels || {};
    facetDefs = [];
    SHARED_FACET_KEYS.forEach(function (key) {
      var dataKey = key;
      var vals = f[dataKey] || [];
      if (!vals.length) return;
      facetDefs.push({
        key: key, dataKey: dataKey, group: 'shared', label: facetLabel(dataKey, labels[dataKey] || key),
        options: vals.map(function (v) { return { v: v, label: valueLabel(dataKey, v) }; })
      });
    });
    var silVals = f.dress_silhouette || [];
    if (silVals.length) {
      facetDefs.push({
        key: 'silhouette', dataKey: 'dress_silhouette', group: 'wedding', label: facetLabel('dress_silhouette', labels.dress_silhouette || 'Dress style'),
        options: silVals.map(function (v) { return { v: v, label: valueLabel('dress_silhouette', v) }; })
      });
    }
    var veilVals = f.veil_length || [];
    if (veilVals.length) {
      facetDefs.push({
        key: 'veil_length', dataKey: 'veil_length', group: 'veil', label: facetLabel('veil_length', labels.veil_length || 'Veil length'),
        options: veilVals.map(function (v) { return { v: v, label: valueLabel('veil_length', v) }; })
      });
    }
  }
  function facetDef(key) {
    for (var i = 0; i < facetDefs.length; i++) if (facetDefs[i].key === key) return facetDefs[i];
    return null;
  }
  function optionLabel(key, v) {
    var def = facetDef(key);
    if (!def) return valueLabel(key, v);
    for (var i = 0; i < def.options.length; i++) if (def.options[i].v === v) return def.options[i].label;
    return valueLabel(def.dataKey, v);
  }

  var LENS_LABELS = {
    portraits: safeI18n.t('js.rb.archive_lens_portraits') || 'Portraits',
    veil: safeI18n.t('js.rb.archive_lens_veil') || 'The veil',
    details: safeI18n.t('js.rb.archive_lens_details') || 'Details',
    flowers: safeI18n.t('js.rb.archive_lens_flowers') || 'Flowers',
    ceremony: safeI18n.t('js.rb.archive_lens_ceremony') || 'Ceremony',
    cake: safeI18n.t('js.rb.archive_lens_cake') || 'Cake and table',
    dancing: safeI18n.t('js.rb.archive_lens_dancing') || 'Dancing'
  };

  function productList() {
    return (data.archive && data.archive.products) || [];
  }
  function productName(h) {
    var idx = productIndexByHandle[h];
    if (idx === undefined) return h;
    var p = productList()[idx];
    return p ? (p.s || p.t) : h;
  }
  function buildProductIndex() {
    productIndexByHandle = {};
    productList().forEach(function (p, i) { productIndexByHandle[p.h] = i; });
  }

  // ---------------------------------------------------------------
  // Filtering predicates — shared by both views' renderers and by this
  // file's own result counts (for tracking). `w` = a wedding record from
  // data.archive.weddings; `p` = a photo record from data.photos.photos.
  // ---------------------------------------------------------------
  function matchesFacet(valuesObj, key, dataKey) {
    var want = state[key];
    if (!want) return true;
    var have = (valuesObj || {})[dataKey || key] || [];
    return have.indexOf(want) > -1;
  }
  function matchesWeddingFacets(w) {
    if (!matchesFacet(w.f, 'setting')) return false;
    if (!matchesFacet(w.f, 'season')) return false;
    if (!matchesFacet(w.f, 'palette')) return false;
    if (!matchesFacet(w.f, 'silhouette', 'dress_silhouette')) return false;
    if (!matchesFacet(w.f, 'veil_length')) return false;
    if (state.country && w.cc !== state.country) return false;
    return true;
  }
  function weddingHasProduct(w, handle) {
    if (!handle) return true;
    var prods = w.pr || [];
    for (var i = 0; i < prods.length; i++) {
      var p = productList()[prods[i][0]];
      if (p && p.h === handle) return true;
    }
    return false;
  }
  function weddingMatchesText(w, q) {
    if (!q) return true;
    var hay = foldText((w.b || '') + ' ' + (w.vn || '') + ' ' + (w.rg || '') + ' ' + (w.ct || ''));
    return hay.indexOf(foldText(q)) > -1;
  }
  function matchesWedding(w) {
    return matchesWeddingFacets(w) && weddingHasProduct(w, state.product) && weddingMatchesText(w, state.q);
  }
  function filteredWeddings() {
    var list = (data.archive && data.archive.weddings) || [];
    return list.filter(matchesWedding);
  }

  function photoHasProduct(p, handle) {
    if (!handle) return true;
    var prods = p.pr || [];
    for (var i = 0; i < prods.length; i++) {
      var prod = productList()[prods[i][0]];
      if (prod && prod.h === handle) return true;
    }
    return false;
  }
  function photoWedding(p) {
    var list = (data.archive && data.archive.weddings) || [];
    return (p.w !== null && p.w !== undefined) ? list[p.w] : null;
  }
  // The alt text of a photograph. On an English page it is the generated `a`
  // field exactly as written (what the page has always said, whatever the
  // photo). On any other language it is built here from keys and the photo's
  // own data: "velo «Luisa» en una boda en Kerry".
  //  - The subject is the photo's first listed piece, only when that piece is
  //    flagged as confirmed for THIS photograph (p.pr[0][1]). It is named by its
  //    short name (p.s: "Luisa"), never the English product title, and a veil
  //    is written through a key ("velo «Luisa»"). A confirmed piece the archive
  //    has no record of (a few custom-veil photos) reads as the plain subject.
  //  - The place is the region as stored ("Kerry"). A region that is only the
  //    country's name (61 weddings store "United Kingdom" there) is the
  //    country in the page's language, as is a wedding with no region.
  function photoAlt(p) {
    if (IS_EN) return p.a || '';
    var w = photoWedding(p);
    var pr = p.pr || [];
    var prod = (pr.length && pr[0][1]) ? productList()[pr[0][0]] : null;
    var subject = '';
    if (prod) {
      var piece = prod.s || prod.t;
      subject = prod.ty === 'Veil' ? (safeI18n.t('rb.archive_alt_veil', { veil: piece }) || piece + ' veil') : piece;
    }
    var place = '';
    if (w) {
      var region = w.rg && w.rg !== w.ct ? w.rg : '';
      place = region || regionName(w.cc) || w.rg || w.ct || '';
    }
    var alt = (subject || safeI18n.t('rb.archive_alt_subject') || 'Real Britten bride') + ' ' +
      (safeI18n.t('rb.archive_alt_wedding') || 'at a wedding');
    if (place) alt += ' ' + (safeI18n.t('rb.archive_alt_in') || 'in') + ' ' + place;
    return alt;
  }
  function photoMatchesFacets(p) {
    var w = photoWedding(p);
    var wf = w ? w.f : {};
    if (state.setting && !matchesFacet(wf, 'setting')) return false;
    if (state.season && !matchesFacet(wf, 'season')) return false;
    if (state.palette && !matchesFacet(wf, 'palette')) return false;
    if (state.country && (!w || w.cc !== state.country)) return false;
    if (state.lens && (p.l || []).indexOf(state.lens) === -1) return false;
    return true;
  }
  function photoSearchText(p) {
    if (p._search) return p._search;
    var w = photoWedding(p);
    var parts = [w ? w.b : '', w ? w.vn : '', w ? w.rg : '', w ? w.ct : ''];
    (p.pr || []).forEach(function (pr) {
      var prod = productList()[pr[0]];
      if (prod) { parts.push(prod.t); parts.push(prod.s); }
    });
    p._search = foldText(parts.filter(Boolean).join(' '));
    return p._search;
  }
  function photoMatchesText(p, q) {
    if (!q) return true;
    return photoSearchText(p).indexOf(foldText(q)) > -1;
  }
  function matchesPhoto(p) {
    return photoMatchesFacets(p) && photoHasProduct(p, state.product) && photoMatchesText(p, state.q);
  }
  function filteredPhotos() {
    var list = (data.photos && data.photos.photos) || [];
    return list.filter(matchesPhoto);
  }

  function anyFilterActive() {
    if (state.q || state.product) return true;
    return ALL_FACET_KEYS.some(function (k) { return !!state[k]; });
  }
  // Same, plus the two Photos-only axes (lens, colour sort) — neither adds
  // a chip (see renderChips), so .brap-clear ("Clear all", assets/britten-
  // archive-photos.js) needs its own, broader check for "is anything
  // active at all right now".
  function anyFilterOrSortActive() {
    return anyFilterActive() || !!state.lens || !!state.colour;
  }

  // ---------------------------------------------------------------
  // Search interpretation — ported from britten-real-brides-page.liquid's
  // interpretQuery(), trimmed to this build's single-select facets, with
  // two audit fixes baked in from the start rather than patched on:
  //   - a submitted search REPLACES the previous interpretation (state is
  //     cleared before the new one is applied — see runSearch below);
  //   - "no X" / "without X" is recognised as an exclusion and explained,
  //     never silently answered with X included.
  // ---------------------------------------------------------------
  var NL_SYNONYMS = [
    [/\bneutrals?\b|\bsoft neutral|\btaupe\b|\bbeige\b/, 'palette', 'neutrals'],
    [/\bjewel\b|\bjewel.?tone/, 'palette', 'jewel_tones'],
    [/\bwhites?\b|\bcreams?\b|\bivory\b/, 'palette', 'whites_creams'],
    [/\bblues?\b|\bnavy\b/, 'palette', 'blues'],
    [/\bgreens?\b|\bsage\b|\bolive\b/, 'palette', 'greens'],
    [/\bautumnal\b|\brust\b|\bterracotta\b|\bburnt orange\b/, 'palette', 'autumnal'],
    [/\bpastels?\b|\bblush\b|\bsoft pink\b|\bpinks?\b/, 'palette', 'neutrals'],
    [/\bcolourful\b|\bcolorful\b|\bbright\b/, 'palette', 'colourful'],
    [/\bautumn\b|\bfall\b/, 'season', 'autumn'],
    [/\bspring\b/, 'season', 'spring'],
    [/\bsummer\b/, 'season', 'summer'],
    [/\bwinter\b|\bchristmas\b/, 'season', 'winter'],
    [/\bchurch\b|\bchapel ceremony\b|\bcathedral wedding\b/, 'setting', 'church'],
    [/\bbarn\b/, 'setting', 'barn'],
    [/\bgardens?\b|\boutdoors?\b/, 'setting', 'garden'],
    [/\bmanor\b|\bcountry house\b|\bestate\b|\bstately\b/, 'setting', 'country_house'],
    [/\bhotel\b/, 'setting', 'hotel'],
    [/\bcastle\b/, 'setting', 'castle'],
    [/\bbeach\b|\bseaside\b/, 'setting', 'beach'],
    [/\bvineyard\b|\bwinery\b/, 'setting', 'vineyard'],
    [/\bwoodland\b|\bforest\b/, 'setting', 'woodland'],
    [/\bmarquee\b|\btipi\b|\btepee\b/, 'setting', 'marquee'],
    [/\bcity\b|\burban\b/, 'setting', 'city'],
    [/\bregistry office\b|\bcity hall\b/, 'setting', 'registry_office'],
    [/\bcathedral( length)? veil\b|\blong(est)? veil\b/, 'veil_length', 'cathedral'],
    [/\bchapel( length)? veil\b/, 'veil_length', 'chapel'],
    [/\bfingertip\b|\bshort veil\b/, 'veil_length', 'fingertip'],
    [/\bfloor.?length\b/, 'veil_length', 'floor'],
    [/\belbow\b/, 'veil_length', 'elbow'],
    [/\bshoulder( length)?\b/, 'veil_length', 'shoulder'],
    [/\bballgown\b|\bball gown\b|\bprincess dress\b/, 'silhouette', 'ballgown'],
    [/\ba.?line\b/, 'silhouette', 'a_line'],
    [/\bfitted\b|\bfit.?and.?flare\b/, 'silhouette', 'fit_and_flare'],
    [/\bmermaid\b/, 'silhouette', 'mermaid'],
    [/\bsheath\b|\bslip dress\b/, 'silhouette', 'sheath'],
    [/\bcolumn\b/, 'silhouette', 'column'],
    [/\btea.?length\b/, 'silhouette', 'tea_length'],
    // Photo-only "moment" words -> lens. Applied regardless of view; a
    // Weddings-view search that mentions "flowers" still sets state.lens
    // (shown as a disabled "photos only" chip there — never dropped).
    [/\bportraits?\b/, 'lens', 'portraits'],
    [/\bflowers?\b|\bbouquet\b/, 'lens', 'flowers'],
    [/\bceremony\b|\baisle\b/, 'lens', 'ceremony'],
    [/\bcake\b/, 'lens', 'cake'],
    [/\bdancing\b|\bdance floor\b/, 'lens', 'dancing'],
    [/\bdetails?\b/, 'lens', 'details']
  ];
  var NL_COUNTRY_ADJ = {
    american: 'US', usa: 'US', states: 'US', british: 'GB', uk: 'GB', england: 'GB',
    english: 'GB', scotland: 'GB', scottish: 'GB', wales: 'GB', welsh: 'GB', irish: 'IE',
    australian: 'AU', french: 'FR', italian: 'IT', spanish: 'ES', german: 'DE', greek: 'GR',
    portuguese: 'PT', icelandic: 'IS', kiwi: 'NZ', canadian: 'CA'
  };
  var NL_STOPWORDS = {
    a: 1, an: 1, the: 1, in: 1, at: 1, on: 1, of: 1, and: 1, with: 1, our: 1, my: 1,
    we: 1, are: 1, is: 1, was: 1, were: 1, will: 1, be: 1, have: 1, having: 1,
    planning: 1, plan: 1, wedding: 1, weddings: 1, bride: 1, brides: 1, day: 1,
    big: 1, 'for': 1, to: 1, next: 1, this: 1, year: 1, style: 1, veil: 1, veils: 1,
    dress: 1, getting: 1, married: 1, marrying: 1, venue: 1, find: 1, show: 1, me: 1,
    setting: 1, place: 1
  };
  var NL_NEGATION_RE = /\b(no|not|without|excluding|except)\b\s+[a-z]/i;

  function isUnsupportedExclusion(text) {
    return NL_NEGATION_RE.test(' ' + (text || '').toLowerCase() + ' ');
  }

  function facetValueExists(key, v) {
    var def = facetDef(key);
    if (!def) return false;
    for (var i = 0; i < def.options.length; i++) if (def.options[i].v === v) return true;
    return false;
  }

  // "jardín" -> "jardin": decompose, drop the combining marks. Done before the
  // [a-z0-9] filter below, which would otherwise turn the accent into a gap
  // ("jard n").
  function foldAccents(s) {
    s = String(s || '');
    return s.normalize ? s.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : s;
  }
  // Lower case, accents gone: the one form a query and the text it is compared
  // with are both put in, so "château", "chateau" and "CHÂTEAU" all meet.
  function foldText(s) { return foldAccents(String(s || '').toLowerCase()); }
  function queryWords(text) {
    return foldText(text).replace(/[^a-z0-9\s'-]/g, ' ').split(/\s+/).filter(Boolean);
  }

  // The language's search words. Each CONCEPT below is one thing the English
  // parser already understands (id -> the English words it is told). A language
  // supplies only the local words for it, as a comma-separated list, in the
  // locale key britten.rb.archive_syn_<id> (e.g. es "jardín, jardines"); the
  // English words live here and are never translated. snippets/britten-archive.
  // liquid prints the page's lists into <script id="bra-synonyms"> for non-
  // English pages only; English never reads them. An empty or missing list is
  // skipped, and a list left in English only maps English to itself. Built
  // once, longest phrases first so "casa de campo" wins over "casa". An id
  // that is not in this table is ignored.
  var SYNONYM_CONCEPTS = {
    neutrals: 'neutrals', jewel_tones: 'jewel tones', ivory: 'ivory', navy: 'navy', sage: 'sage',
    terracotta: 'terracotta', blush: 'blush', colourful: 'colourful',
    autumn: 'autumn', spring: 'spring', summer: 'summer', winter: 'winter',
    church: 'church', barn: 'barn', garden: 'garden', country_house: 'country house', hotel: 'hotel',
    castle: 'castle', beach: 'beach', vineyard: 'vineyard', woodland: 'woodland', marquee: 'marquee',
    city: 'city', registry_office: 'registry office',
    cathedral_veil: 'cathedral veil', chapel_veil: 'chapel veil', fingertip: 'fingertip', floor_length: 'floor length',
    elbow: 'elbow', shoulder: 'shoulder', mantilla: 'mantilla',
    ballgown: 'ballgown', a_line: 'a-line', fitted: 'fitted', mermaid: 'mermaid', sheath: 'sheath', column: 'column',
    tea_length: 'tea length',
    portraits: 'portraits', flowers: 'flowers', ceremony: 'ceremony', cake: 'cake', dancing: 'dancing', details: 'details',
    spain: 'spain', italy: 'italy', france: 'france', greece: 'greece', portugal: 'portugal', germany: 'germany',
    ireland: 'ireland', iceland: 'iceland', croatia: 'croatia', poland: 'poland', belgium: 'belgium',
    australia: 'australia', new_zealand: 'new zealand', canada: 'canada', united_states: 'united states',
    united_kingdom: 'united kingdom', england: 'england', scotland: 'scotland', wales: 'wales', tuscany: 'tuscany',
    wedding: 'wedding', bride: 'bride', dress: 'dress', veil: 'veil',
    without: 'without', filler: ''
  };
  var synonymRules = null;
  function synonymLists() {
    try {
      var el = document.getElementById('bra-synonyms');
      var parsed = el ? JSON.parse(el.textContent || '{}') : null;
      return plainObject(parsed);
    } catch (e) { log('search words unreadable: ' + e); return {}; }
  }
  function synonymTable() {
    if (synonymRules) return synonymRules;
    synonymRules = [];
    if (IS_EN) return synonymRules;
    var lists = synonymLists();
    Object.keys(SYNONYM_CONCEPTS).forEach(function (id) {
      var raw = hasOwn(lists, id) ? lists[id] : '';
      if (typeof raw !== 'string' || !raw) return;
      // Liquid's t filter HTML-escapes the value; put it back.
      raw = raw.replace(/&#39;/g, "'").replace(/&quot;/g, '"').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
      raw.split(',').forEach(function (term) {
        var words = queryWords(term);
        if (words.length) synonymRules.push({ words: words, to: SYNONYM_CONCEPTS[id] });
      });
    });
    synonymRules.sort(function (a, b) { return b.words.length - a.words.length; });
    return synonymRules;
  }
  // The query as English words, for interpretQuery(). English and a language
  // with no table come back untouched.
  function translateQuery(text) {
    var rules = synonymTable();
    var words = queryWords(text);
    if (!rules.length || !words.length) return text;
    var out = [], i = 0;
    while (i < words.length) {
      var hit = null;
      for (var r = 0; r < rules.length && !hit; r++) {
        var rw = rules[r].words, k = 0;
        if (i + rw.length > words.length) continue;
        while (k < rw.length && words[i + k] === rw[k]) k++;
        if (k === rw.length) hit = rules[r];
      }
      if (hit) { if (hit.to) out.push(hit.to); i += hit.words.length; } else { out.push(words[i]); i++; }
    }
    return out.join(' ');
  }
  // A word we did not recognise, shown as she typed it (accents and all).
  function typedWords(text) {
    var typed = {};
    String(text || '').toLowerCase().split(/\s+/).forEach(function (raw) {
      var w = raw.replace(/^[^a-z0-9À-ɏ]+|[^a-z0-9À-ɏ]+$/g, '');
      if (w) typed[foldAccents(w)] = w;
    });
    return typed;
  }

  function interpretQuery(text) {
    var out = { facets: {}, lens: null, product: null, free: '', unmatched: [] };
    var t = ' ' + foldText(text).replace(/[^a-z0-9\s'-]/g, ' ').replace(/\s+/g, ' ').trim() + ' ';
    if (t === '  ' || t === ' ') return out;

    var consumedWords = {};
    function markConsumed(pattern) {
      var m = pattern.exec(t);
      // best-effort: mark every stop/keyword token the pattern's own source
      // mentions, so it can't also show up as "unmatched". Strip \b/\B
      // (and other backslash-escapes) FIRST — left in, a source like
      // \bbarn\b reads as one run "bbarn" (the escaped b fuses with the
      // real word), which fails to mark "barn" itself as consumed.
      var cleaned = pattern.source.replace(/\\[a-zA-Z]/g, ' ');
      (cleaned.match(/[a-z]{4,}/g) || []).forEach(function (w) { consumedWords[w] = true; });
      return m;
    }
    function add(key, v) {
      if (key === 'lens') {
        out.lens = v;
        return;
      }
      if (!facetValueExists(key, v)) return;
      out.facets[key] = v; // single-select: last match for a key wins
    }
    NL_SYNONYMS.forEach(function (rule) {
      if (rule[0].test(t)) { add(rule[1], rule[2]); markConsumed(rule[0]); }
    });

    // Country: full names + adjectives.
    var countries = (data.archive && data.archive.facets && data.archive.facets.countryNames) || {};
    Object.keys(countries).forEach(function (cc) {
      var name = foldText(countries[cc]);
      if (name && t.indexOf(' ' + name + ' ') > -1) { out.facets.country = cc; consumedWords[name] = true; }
    });
    Object.keys(NL_COUNTRY_ADJ).forEach(function (adj) {
      if (new RegExp('\\b' + adj + '\\b').test(t)) { out.facets.country = NL_COUNTRY_ADJ[adj]; consumedWords[adj] = true; }
    });

    // Product names, longest first so e.g. "Isabella" doesn't shadow a
    // longer, more specific candidate that also contains it.
    var candidates = productList().map(function (p, i) { return { name: foldText(p.s || p.t || ''), h: p.h, i: i }; })
      .filter(function (c) { return c.name.length > 2; })
      .sort(function (a, b) { return b.name.length - a.name.length; });
    for (var pi = 0; pi < candidates.length; pi++) {
      var pn = candidates[pi].name;
      var re = new RegExp('\\b' + pn.replace(/[^a-z0-9]/g, '.') + '\\b');
      if (re.test(t)) {
        out.product = candidates[pi].h;
        pn.split(' ').forEach(function (w) { consumedWords[w] = true; });
        break;
      }
    }

    // Remaining tokens -> a free-text location chip (bigrams first, e.g.
    // "lake district"), matched against venue/region text. Anything left
    // over after that is reported as unmatched, for the calm explainer
    // line — never silently dropped.
    var tokens = t.trim().split(' ').filter(function (w) {
      return w.length >= 3 && !NL_STOPWORDS[w] && !consumedWords[w];
    });
    var weddings = (data.archive && data.archive.weddings) || [];
    var haystack = weddings.map(function (w) { return foldText((w.vn || '') + ' ' + (w.rg || '') + ' ' + (w.ct || '')); });
    function findsPlace(cand) {
      for (var i = 0; i < haystack.length; i++) if (haystack[i].indexOf(cand) > -1) return true;
      return false;
    }
    var leftover = tokens.slice();
    for (var bi = 0; bi < tokens.length - 1 && !out.free; bi++) {
      var bigram = tokens[bi] + ' ' + tokens[bi + 1];
      if (findsPlace(bigram)) {
        out.free = bigram;
        leftover = leftover.filter(function (w) { return w !== tokens[bi] && w !== tokens[bi + 1]; });
      }
    }
    if (!out.free) {
      for (var ti = 0; ti < tokens.length; ti++) {
        if (findsPlace(tokens[ti])) { out.free = tokens[ti]; leftover = leftover.filter(function (w) { return w !== tokens[ti]; }); break; }
      }
    }
    out.unmatched = leftover;
    return out;
  }

  // ---------------------------------------------------------------
  // Views — weddings.js / photos.js each call registerView() once they've
  // parsed and are ready to render. renderActiveView() is the one place
  // that decides what's on screen; every state mutation below ends by
  // calling it.
  // ---------------------------------------------------------------
  var views = {}; // name -> { render(), destroy() }
  function registerView(name, api) {
    views[name] = api;
    if (state.view === name) renderActiveView();
  }
  var lastRenderedView = null;
  // Section visibility is decided HERE, centrally, regardless of whether
  // the matching view module has registered yet — otherwise switching to
  // Weddings before assets/britten-archive-photos.js has loaded (PR 1/2
  // reviewed alone) would leave the still-default-visible Photos section
  // showing alongside it. Each view's own render()/hide() only fills in
  // content; it never needs to touch its section's `hidden` itself.
  var seoPhotos = document.querySelector('.bra-seo-photos');
  var seoWeddings = document.querySelector('.bra-seo-weddings');
  function showOnlyActiveSection() {
    root.querySelectorAll('[data-bra-view]').forEach(function (sec) {
      sec.hidden = sec.getAttribute('data-bra-view') !== state.view;
    });
    // The generated SEO block (snippets/britten-archive-first.liquid) is
    // rendered ONCE, both sub-blocks together, with no idea which view is
    // active — this is the only place that decides which one shows. Each
    // view's own JS additionally hides its sub-block for good once its
    // real content has rendered (hideSeoOnce in britten-archive-photos.js
    // / britten-archive-weddings.js) — this toggle alone would otherwise
    // re-reveal it on every render.
    if (seoPhotos && seoPhotos.getAttribute('data-bra-seo-done') !== '1') {
      seoPhotos.hidden = state.view !== 'photos';
    }
    if (seoWeddings && seoWeddings.getAttribute('data-bra-seo-done') !== '1') {
      seoWeddings.hidden = state.view !== 'weddings';
    }
  }
  function renderActiveView() {
    showOnlyActiveSection();
    Object.keys(views).forEach(function (name) {
      if (name === state.view) return;
      if (views[name] && typeof views[name].hide === 'function') views[name].hide();
    });
    var v = views[state.view];
    if (v && typeof v.render === 'function') v.render();
    renderHeader();
    renderChips();
    lastRenderedView = state.view;
    changeListeners.forEach(function (fn) { try { fn(); } catch (e) { log('onChange listener failed: ' + e); } });
  }
  // Tiny pub/sub for the header's upload (britten-archive-weddings.js): it
  // needs to know when state changed without owning any of it.
  var changeListeners = [];
  var clearListeners = [];

  // ---------------------------------------------------------------
  // Header — H1 + count line, view switch, search, example chips, chips
  // row, "Match my colours".
  // ---------------------------------------------------------------
  var countEl = root.querySelector('[data-bra-count]');
  var switchBtns = root.querySelectorAll('[data-bra-switch]');

  function renderHeader() {
    switchBtns.forEach(function (btn) {
      var v = btn.getAttribute('data-bra-switch');
      var on = v === state.view;
      btn.classList.toggle('bra-switch__btn--on', on);
      btn.setAttribute('aria-current', on ? 'true' : 'false');
    });
    if (!countEl) return;
    // Until the JSON is here, leave whatever the server rendered (the
    // generated counts snippet) — blanking it would undo the point of it.
    if (!data.archive) return;
    // Round 3: always both counts, straight from the JSON — never
    // hard-coded, never narrowed by a filter or by the active view.
    countEl.textContent = safeI18n.t('js.rb.count_all', { weddings: fmt(data.archive.n_weddings), photographs: fmt(data.archive.n_photos) }) ||
      fmt(data.archive.n_weddings) + ' weddings \u00b7 ' + fmt(data.archive.n_photos) + ' photographs';
  }

  function switchView(name, opts) {
    if (state.view === name) return;
    state.view = name;
    writeUrl(!(opts && opts.replace));
    renderActiveView();
    track('switch', { to: name });
    if (!(opts && opts.quiet)) track('view', { view: name });
  }
  switchBtns.forEach(function (btn) {
    btn.addEventListener('click', function () { switchView(btn.getAttribute('data-bra-switch')); });
  });

  // ---- interpretation / active chips ----
  var chipsEl = root.querySelector('[data-bra-chips]');
  var noticeEl = root.querySelector('[data-bra-notice]');
  var lastUnmatched = [];
  var lastUnsupported = '';

  function makeChip(label, onRemove, opts) {
    var chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'bra-chip' + (opts && opts.disabled ? ' bra-chip--disabled' : '');
    if (opts && opts.disabled) chip.disabled = true;
    chip.appendChild(document.createTextNode(label));
    if (!(opts && opts.disabled)) {
      var x = document.createElement('span');
      x.className = 'bra-chip__x';
      x.setAttribute('aria-hidden', 'true');
      x.textContent = '✕';
      chip.appendChild(x);
      chip.addEventListener('click', onRemove);
      chip.setAttribute('aria-label', safeI18n.t('js.rb.archive_chip_remove_aria', { label: label }) || 'Remove filter: ' + label);
    } else {
      chip.setAttribute('aria-label', label);
    }
    return chip;
  }

  function renderChips() {
    if (!chipsEl) return;
    chipsEl.innerHTML = '';
    var any = false;
    ALL_FACET_KEYS.forEach(function (key) {
      if (!state[key]) return;
      any = true;
      var label = optionLabel(key, state[key]);
      chipsEl.appendChild(makeChip(label, function () { setFacet(key, ''); }));
    });
    if (state.product) {
      any = true;
      chipsEl.appendChild(makeChip(productName(state.product), function () { setProduct(''); }));
    }
    if (state.q) {
      any = true;
      chipsEl.appendChild(makeChip(safeI18n.t('js.rb.search_chip_label', { query: state.q }) || '“' + state.q + '”', function () { setQuery(''); }));
    }
    // Round 1 owner feedback: a colour (spectrum/shade) or a moment (e.g.
    // "The veil") never adds a chip here — each already shows its own "on"
    // state on the control itself (the spectrum thumb / the lens pill).
    // .brap-clear ("Clear all", assets/britten-archive-photos.js) is the
    // one thing that reflects them being active.
    if (any) {
      var clr = document.createElement('button');
      clr.type = 'button';
      clr.className = 'bra-chips__clear';
      clr.textContent = safeI18n.t('js.rb.clear_all') || 'Clear all';
      clr.addEventListener('click', clearAll);
      chipsEl.appendChild(clr);
    }
    chipsEl.hidden = !any && !lastUnmatched.length && !lastUnsupported;
    renderNotice();
  }

  function renderNotice() {
    if (!noticeEl) return;
    if (lastUnsupported) {
      noticeEl.hidden = false;
      noticeEl.textContent = safeI18n.t('js.rb.archive_notice_exclude', { query: lastUnsupported }) ||
        'We can’t search for what to leave out yet — showing results for the rest of "' + lastUnsupported + '".';
      return;
    }
    if (lastUnmatched.length) {
      noticeEl.hidden = false;
      noticeEl.textContent = safeI18n.t('js.rb.archive_notice_unmatched', { words: lastUnmatched.join(', ') }) ||
        'Didn’t recognise: ' + lastUnmatched.join(', ') + '.';
      return;
    }
    noticeEl.hidden = true;
    noticeEl.textContent = '';
  }

  // ---------------------------------------------------------------
  // Public mutators — every one re-renders, writes the URL and tracks.
  // ---------------------------------------------------------------
  function setFacet(key, value, opts) {
    state[key] = value || '';
    writeUrl(false);
    renderActiveView();
    if (value && !(opts && opts.quiet)) track('filter', { type: key, value: value, view: state.view });
  }
  function setProduct(handle, opts) {
    state.product = handle || '';
    writeUrl(false);
    renderActiveView();
    if (handle && !(opts && opts.quiet)) track('filter', { type: 'product', value: handle, view: state.view });
  }
  function setLens(id) {
    state.lens = id || '';
    writeUrl(false);
    renderActiveView();
    if (id) track('filter', { type: 'lens', value: id, view: state.view });
  }
  function setColour(v) {
    state.colour = v || '';
    writeUrl(false);
    renderActiveView();
    if (v) track('filter', { type: 'colour', value: v, view: state.view });
  }
  function setQuery(q, opts) {
    state.q = q || '';
    writeUrl(false);
    renderActiveView();
    if (!(opts && opts.quiet)) trackSearchResults(q);
  }
  function trackSearchResults(q) {
    var n = state.view === 'weddings' ? filteredWeddings().length : (data.photos ? filteredPhotos().length : null);
    if (n === null) {
      // photos.json not loaded yet (deferred) — track once it lands.
      pendingSearchTrack = q;
      return;
    }
    track('search', { q: q, results: n, view: state.view, zero: n === 0 });
  }
  var pendingSearchTrack = null;

  function clearAll() {
    STATE_KEYS.forEach(function (k) { if (k !== 'view' && k !== 'p' && k !== 'w') state[k] = ''; });
    lastUnmatched = [];
    lastUnsupported = '';
    if (searchInput) searchInput.value = '';
    clearListeners.forEach(function (fn) { try { fn(); } catch (e) { log('onClearAll listener failed: ' + e); } });
    writeUrl(false);
    renderActiveView();
  }

  // `englishQuery` (optional) is the English search a translated "Try" example
  // stands for: the box shows what she clicked, the search runs on the English.
  function runSearch(text, englishQuery) {
    text = (text || '').trim();
    if (searchInput) searchInput.value = text;
    if (!text) { clearAll(); return; }
    var query = englishQuery || translateQuery(text);
    if (isUnsupportedExclusion(query)) {
      lastUnsupported = text;
      lastUnmatched = [];
      renderChips();
      return;
    }
    var r = interpretQuery(query);
    // REPLACE, not merge (audit fix): every shared/veil facet, product and
    // lens starts clean before this search's own matches are applied.
    ALL_FACET_KEYS.forEach(function (k) { state[k] = ''; });
    state.product = '';
    state.lens = '';
    Object.keys(r.facets).forEach(function (k) { state[k] = r.facets[k]; });
    if (r.lens) state.lens = r.lens;
    if (r.product) state.product = r.product;
    state.q = r.free || '';
    lastUnsupported = '';
    lastUnmatched = r.unmatched || [];
    if (!IS_EN && lastUnmatched.length) {
      var typed = typedWords(text);
      lastUnmatched = lastUnmatched.map(function (w) { return hasOwn(typed, w) ? typed[w] : w; });
    }
    writeUrl(false);
    renderActiveView();
    trackSearchResults(text);
  }

  // ---------------------------------------------------------------
  // Lightbox open/close hooks — the actual dialog is rendered by
  // assets/britten-archive-lightbox.js (PR 3). Until that file registers
  // itself, these just move state/the URL; nothing visible happens yet.
  // ---------------------------------------------------------------
  var overlay = null; // { render(id|slug, opts), close() } — set by registerOverlay
  function registerOverlay(api) {
    overlay = api;
    // A direct ?p=/?w= arrival, or a refresh while one was open, may reach
    // this point before the lightbox module has finished loading (it's
    // loaded after first paint) — open it the moment it's ready, without
    // touching the URL again (it's already correct).
    if (state.p && overlay.openPhoto) overlay.openPhoto(state.p);
    else if (state.w && overlay.openWedding) overlay.openWedding(state.w);
  }

  function openPhoto(id, opts) {
    state.p = id;
    state.w = '';
    var push = !(opts && opts.push === false);
    writeOverlayUrl(push);
    if (overlay && overlay.openPhoto) overlay.openPhoto(id);
    track('open', { kind: 'photo', id: id });
  }
  function openWedding(slug, opts) {
    state.w = slug;
    state.p = '';
    var push = !(opts && opts.push === false);
    writeOverlayUrl(push);
    if (overlay && overlay.openWedding) overlay.openWedding(slug);
    track('open', { kind: 'wedding', id: slug });
  }
  function closeOverlay() {
    if (overlay && overlay.close) overlay.close();
    // A direct ?p=/?w= arrival must close to the archive, never
    // history.back() out of it (audit: "direct-photo close"). Only go
    // back if THIS page pushed the entry that's open right now.
    if (history.state && history.state.braOverlay && history.state.braPushed) {
      history.back();
    } else {
      state.p = '';
      state.w = '';
      writeUrl(false);
    }
  }

  window.addEventListener('popstate', function () {
    state = stateFromUrl(currentUrl());
    if (searchInput) searchInput.value = state.q || '';
    renderActiveView();
    if (state.p && overlay && overlay.openPhoto) overlay.openPhoto(state.p);
    else if (state.w && overlay && overlay.openWedding) overlay.openWedding(state.w);
    else if (overlay && overlay.close) overlay.close();
  });

  // ---------------------------------------------------------------
  // Search box + "Try" examples
  // ---------------------------------------------------------------
  var searchInput = root.querySelector('[data-bra-search]');
  var searchGo = root.querySelector('[data-bra-search-go]');
  if (searchInput) {
    searchInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); runSearch(searchInput.value); }
    });
  }
  if (searchGo) searchGo.addEventListener('click', function () { runSearch(searchInput ? searchInput.value : ''); });
  // "Try" examples (desktop only — CSS): fill the box and run it, exactly as
  // if it had been typed and submitted.
  // The label can be translated; the search that runs is always the English one.
  var TRY_QUERIES = ['Cathedral veil in Tuscany', 'Winter church wedding', 'Navy bridesmaids', 'Barely-there veil, fitted gown'];
  root.querySelectorAll('[data-bra-try]').forEach(function (btn, i) {
    btn.addEventListener('click', function () { runSearch(btn.textContent, IS_EN ? '' : TRY_QUERIES[i]); });
  });

  // ---------------------------------------------------------------
  // Photos default order (round 1 owner feedback — order bug)
  // ---------------------------------------------------------------
  // Same base ordering as rank_key() in MarketingOS pipeline/
  // build_picture_desk_data.py — the two MUST agree: photos with an opening
  // rank (`e`, 1..30) first, in that order; everything else newest wedding
  // first (the photo's wedding `an`, its archive number, highest first; a
  // photo with no wedding record counts as 0, so it goes last), then quality
  // desc, id as a stable tiebreak. That is rank_key's (-an, -q, id) for a
  // photo with no opening rank. The generated first-paint tiles (snippets/
  // britten-archive-first.liquid) are already built from that same rank_key,
  // so the first 30 here always agree with what a visitor already saw before
  // this JSON loaded — this only reorders the REST, plus interleaves it (see
  // below). Applied once, to the base array, so every downstream read
  // (filtered pool, spectrum, lens counts) already sees the right order —
  // colour-sort (assets/britten-archive-photos.js orderedPhotos()) still
  // overrides it when a hue is picked, same as before.
  // `an` lives on the wedding record, not the photo: photoWedding(p) is
  // data.archive.weddings[p.w], so the archive JSON must be loaded first
  // (loadPhotosViewAssets() waits for archiveSettled).
  function idCompare(a, b) { return a < b ? -1 : a > b ? 1 : 0; }
  function weddingArchiveNo(p) {
    var w = photoWedding(p);
    return (w && Number(w.an)) || 0;
  }
  function canonicalPhotoOrder(photos) {
    var opening = [], rest = [];
    (photos || []).forEach(function (p) { (p.e != null ? opening : rest).push(p); });
    opening.sort(function (a, b) { return (a.e - b.e) || idCompare(a.id, b.id); });
    rest.sort(function (a, b) {
      return (weddingArchiveNo(b) - weddingArchiveNo(a)) || ((b.q || 0) - (a.q || 0)) || idCompare(a.id, b.id);
    });

    // After the first 30: don't show two photos from the same wedding
    // back-to-back where avoidable. Bucket the (already newest-wedding-first,
    // then quality-ordered) rest by wedding — buckets are made in order of
    // first appearance, so the newest wedding's bucket comes first — then
    // round-robin one photo per bucket per pass: each pass runs newest
    // wedding first. Deterministic, and only ever puts the same wedding
    // adjacent to itself if it's more than half of what's left (unavoidable).
    var buckets = [], byWedding = {};
    rest.forEach(function (p) {
      var key = p.w;
      if (!byWedding[key]) { byWedding[key] = []; buckets.push(byWedding[key]); }
      byWedding[key].push(p);
    });
    var interleaved = [], left = rest.length;
    while (left > 0) {
      for (var i = 0; i < buckets.length; i++) {
        if (!buckets[i].length) continue;
        interleaved.push(buckets[i].shift());
        left--;
      }
    }
    return opening.concat(interleaved);
  }

  // ---------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------
  var ARCHIVE_URL = root.getAttribute('data-archive-json');
  var PHOTOS_URL = root.getAttribute('data-photos-json');

  // Settles (never rejects) once the archive fetch has finished, whether it
  // worked or not. The photos JSON waits for it before it is put in order,
  // because the order needs each photo's wedding (see canonicalPhotoOrder).
  var archiveSettled = Promise.resolve();
  function loadArchive() {
    if (!ARCHIVE_URL) return;
    // English: no second request. Otherwise the language file is fetched at the
    // same time and waited for (it never fails the page: a missing or broken
    // file resolves to null and the page stays English).
    var overlayWanted = fetchOverlay();
    archiveSettled = fetch(ARCHIVE_URL).then(function (r) { return r.json(); }).then(function (d) {
      return overlayWanted.then(function (t) { applyOverlay(d, t); return d; });
    }).then(function (d) {
      data.archive = d;
      buildProductIndex();
      buildFacetDefs();
      renderHeader();
      renderChips();
      renderActiveView();
      if (pendingSearchTrack !== null) { var q = pendingSearchTrack; pendingSearchTrack = null; trackSearchResults(q); }
    }).catch(function (e) { log('archive fetch failed: ' + e); });
  }

  // The photos JSON (~1MB) and assets/britten-archive-photos.js load after
  // first paint, never blocking it — the server-rendered first tiles (see
  // snippets/britten-archive-first.liquid) cover first paint regardless of
  // which view a visitor lands on.
  var photosScriptRequested = false;
  // Anything (today: the Weddings cards' thumbnail strips) that needs the
  // photos JSON registers here. Called once the JSON is in data.photos, or —
  // so nothing waits forever — once the fetch has failed (data.photos is
  // then still null and the listener must cope).
  var photoListeners = [];
  var photosFailed = false;
  function firePhotoListeners() {
    var todo = photoListeners;
    photoListeners = [];
    todo.forEach(function (fn) { try { fn(); } catch (e) { log('onPhotosReady listener failed: ' + e); } });
  }
  function loadPhotosViewAssets() {
    if (photosScriptRequested || !PHOTOS_URL) return;
    photosScriptRequested = true;
    fetch(PHOTOS_URL).then(function (r) { return r.json(); }).then(function (d) {
      return archiveSettled.then(function () { return d; });
    }).then(function (d) {
      if (d && d.photos) d.photos = canonicalPhotoOrder(d.photos);
      data.photos = d;
      renderHeader();
      if (state.view === 'photos') renderActiveView();
      if (pendingSearchTrack !== null && state.view === 'photos') { var q = pendingSearchTrack; pendingSearchTrack = null; trackSearchResults(q); }
      firePhotoListeners();
    }).catch(function (e) { log('photos fetch failed: ' + e); photosFailed = true; firePhotoListeners(); });
    var s = document.createElement('script');
    s.src = root.getAttribute('data-photos-script') || '';
    s.defer = true;
    if (s.src) document.body.appendChild(s);
  }
  function scheduleDeferredLoad(fn) {
    if ('requestIdleCallback' in window) requestIdleCallback(fn, { timeout: 2000 });
    else if (document.readyState === 'complete') setTimeout(fn, 0);
    else window.addEventListener('load', function () { setTimeout(fn, 0); }, { once: true });
  }

  // ---------------------------------------------------------------
  // Press marks — an optional `px` on a wedding record:
  //   { b: 'vogue', o: 'Vogue Australia', u: 'https://…' }
  // `b` is the id of a logo in snippets/britten-press-marks.liquid, which
  // prints every logo once as JSON (<script id="britten-press-marks">). Read
  // lazily, parsed once, cached. Every helper below is a quiet no-op — null —
  // when `px` is absent, the JSON is missing or the id is unknown: no badge,
  // no error. They never throw.
  // ---------------------------------------------------------------
  var pressMarksCache = null;
  function pressMarkMap() {
    if (pressMarksCache) return pressMarksCache;
    pressMarksCache = {};
    try {
      var el = document.getElementById('britten-press-marks');
      var parsed = el ? JSON.parse(el.textContent || '{}') : null;
      if (parsed && typeof parsed === 'object') pressMarksCache = parsed;
    } catch (e) { log('press marks unreadable: ' + e); }
    return pressMarksCache;
  }
  function pressMarkSvg(id) {
    var map = pressMarkMap();
    var svg = (typeof id === 'string' && Object.prototype.hasOwnProperty.call(map, id)) ? map[id] : '';
    return (typeof svg === 'string' && /^\s*<svg[\s>]/i.test(svg)) ? svg : '';
  }
  // A fresh inline <svg> for a mark id (with the logo's own role/aria-label),
  // or null for an unknown id.
  function pressMark(id) {
    try {
      var markup = pressMarkSvg(id);
      if (!markup) return null;
      var holder = document.createElement('span');
      holder.innerHTML = markup;
      var svg = holder.firstElementChild;
      if (!svg || String(svg.tagName).toLowerCase() !== 'svg') return null;
      svg.setAttribute('focusable', 'false');
      return svg;
    } catch (e) { return null; }
  }
  // What a wedding's `px` says, checked: { id, label, suffix, url } or null.
  //   label  the outlet's full name ("Vogue Australia"), or the id capitalised
  //   suffix the outlet name after the mark's own name ("Australia", "Adria");
  //          '' when the outlet is just "Vogue" or does not start with it
  //   url    the article, only if it is http(s), else ''
  function press(w) {
    try {
      var px = w && w.px;
      if (!px || typeof px !== 'object') return null;
      var id = typeof px.b === 'string' ? px.b.trim().toLowerCase() : '';
      if (!id || !pressMarkSvg(id)) return null;
      var outlet = typeof px.o === 'string' ? px.o.trim() : '';
      var lead = new RegExp('^' + id.replace(/[^a-z0-9]/g, '\\$&') + '\\s+', 'i');
      var url = typeof px.u === 'string' ? px.u.trim() : '';
      return {
        id: id,
        label: outlet || (id.charAt(0).toUpperCase() + id.slice(1)),
        suffix: lead.test(outlet) ? outlet.replace(lead, '').trim() : '',
        url: /^https?:\/\//i.test(url) ? url : ''
      };
    } catch (e) { return null; }
  }
  // The corner badge: <span class="{cls}" aria-hidden="true"><svg…></span>, the
  // logo purely decorative (its label is dropped). null when the wedding has
  // no press mark. Callers add the accessible "Featured in …" words elsewhere.
  function pressBadge(w, cls) {
    var info = press(w);
    var svg = info && pressMark(info.id);
    if (!svg) return null;
    svg.removeAttribute('role');
    svg.removeAttribute('aria-label');
    var badge = document.createElement('span');
    badge.className = cls;
    badge.setAttribute('aria-hidden', 'true');
    badge.appendChild(svg);
    return badge;
  }

  window.BrittenArchive = {
    track: track,
    getState: function () { return state; },
    data: data,
    registerView: registerView,
    registerOverlay: registerOverlay,
    filteredWeddings: filteredWeddings,
    filteredPhotos: filteredPhotos,
    matchesWedding: matchesWedding,
    matchesPhoto: matchesPhoto,
    photoWedding: photoWedding,
    photoAlt: photoAlt,
    press: press,
    pressMark: pressMark,
    pressBadge: pressBadge,
    productList: productList,
    productName: productName,
    facetDefs: function () { return facetDefs; },
    facetDef: facetDef,
    optionLabel: optionLabel,
    setFacet: setFacet,
    setProduct: setProduct,
    setLens: setLens,
    setColour: setColour,
    runSearch: runSearch,
    clearAll: clearAll,
    switchView: switchView,
    openPhoto: openPhoto,
    openWedding: openWedding,
    closeOverlay: closeOverlay,
    ensurePhotosLoaded: function () { loadPhotosViewAssets(); },
    LENS_LABELS: LENS_LABELS,
    // Language, for the other three archive scripts: the page's language, a
    // whole-number formatter, a country's name from its ISO code ('' in
    // English), and the search steps (also what scripts/archive-search-test.js drives).
    lang: LANG,
    isEnglish: IS_EN,
    fmt: fmt,
    regionName: regionName,
    translateQuery: translateQuery,
    synonymConcepts: Object.keys(SYNONYM_CONCEPTS),
    interpretQuery: interpretQuery,
    anyFilterActive: anyFilterActive,
    anyFilterOrSortActive: anyFilterOrSortActive,
    onChange: function (fn) { if (typeof fn === 'function') changeListeners.push(fn); },
    // True once the photos JSON fetch has failed (nothing will ever arrive).
    photosFailed: function () { return photosFailed; },
    // Runs fn once the photos JSON has loaded (at once if it already has), or
    // once its fetch has failed (data.photos is then still null).
    onPhotosReady: function (fn) {
      if (typeof fn !== 'function') return;
      if (data.photos || photosFailed) { try { fn(); } catch (e) { log('onPhotosReady listener failed: ' + e); } }
      else photoListeners.push(fn);
    },
    onClearAll: function (fn) { if (typeof fn === 'function') clearListeners.push(fn); }
  };

  state = stateFromUrl(currentUrl());
  if (absorbLegacyHash()) { history.replaceState(null, '', location.pathname + location.search); }
  writeUrl(false);
  if (searchInput) searchInput.value = state.q || '';
  showOnlyActiveSection();
  renderHeader();
  renderChips();
  loadArchive();
  scheduleDeferredLoad(loadPhotosViewAssets);
  track('view', { view: state.view });
})();
